import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

export const initialState = {
  postAcqWorkflowGridState: null,
  postAcqWorkflowGridStateUpdate: null,
  postAcqWorkflowGridDataUpdate: null,
  postAcqWorkflowGridLayouts: null,
  postAcqWorkflowGridSelectedLayoutId: null,
  postAcqWorkflowGridLayoutIsDiverted: false,
  channelWorkflowLoadingOverlayState: {
    isLoading: false,
    message: null
  },
  isFetchingPostAcqWorkflowData: false,
  workflowGridNeedsToRefresh: false
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'postAcqWorkflowSlice',
  initialState,
  reducers: {
    setPostAcqWorkflowGridState: { // Optional 2nd paramater will merge instead of replacing state
      reducer: (state, action) => {
        state.postAcqWorkflowGridState = action.payload
      },
      prepare: (state, isUpdate) => ({ payload: { state, isUpdate } })
    },
    setPostAcqWorkflowGridStateUpdate: { // Stash a pending state mutation; PostAcqWorkflowGrid folds it in once mounted with columns
      reducer: (state, action) => {
        state.postAcqWorkflowGridStateUpdate = action.payload
      },
      prepare: (state, isUpdate) => ({ payload: { state, isUpdate } })
    },
    setPostAcqWorkflowGridDataUpdate: (state, action) => {
      state.postAcqWorkflowGridDataUpdate = action.payload;
    },
    setPostAcqWorkflowGridLayouts: (state, action) => {
      state.postAcqWorkflowGridLayouts = action.payload;
    },
    setPostAcqWorkflowGridSelectedLayoutId: { // Triggers channelWorkflowGridLayoutSaga
      reducer: (state, action) => {
        state.postAcqWorkflowGridSelectedLayoutId = action.payload.layout
      },
      prepare: (layout, layoutType, shouldFetch) => ({ payload: { layout, layoutType, shouldFetch } })
    },
    setPostAcqWorkflowGridLayoutIsDiverted: (state, action) => {
      state.postAcqWorkflowGridLayoutIsDiverted = action.payload;
    },
    setPostAcqWorkflowLoadingOverlayState: (state, action) => {
      state.postAcqWorkflowLoadingOverlayState = action.payload;
    },
    setIsFetchingPostAcqWorkflowData: (state, action) => {
      state.isFetchingPostAcqWorkflowData = action.payload;
    },
    setPostAcqWorkflowGridNeedsToRefresh: (state, action) => {
      state.workflowGridNeedsToRefresh = action.payload;
    },

    // Redux saga actions, no reducers
    fetchChannelWorkflowGridLayouts: () => { }, // Triggers channelWorkflowGridLayoutSaga
    saveChannelWorkflowGridLayout: { // Triggers channelWorkflowGridLayoutSaga
      reducer: () => { },
      prepare: (req, layoutType, callback) => ({ payload: { req, layoutType, callback } })
    },
    updateChannelWorkflowGridLayout: { // Triggers channelWorkflowGridLayoutSaga
      reducer: () => { },
      prepare: (req, layoutType, callback) => ({ payload: { req, layoutType, callback } })
    },
    deleteChannelWorkflowGridLayout:  { // Triggers channelWorkflowGridLayoutSaga
      reducer: () => { },
      prepare: (id, layoutType, callback) => ({ payload: { id, layoutType, callback } })
    },
    favoriteChannelWorkflowGridLayout:  { // Triggers channelWorkflowGridLayoutSaga
      reducer: () => { },
      prepare: (id, isFavorite, layoutType, successCallback, errorCallback) => ({ payload: { id, isFavorite, layoutType, successCallback, errorCallback } })
    },
    loadPostAcqTasks: () => { }, // Triggers postAcqTaskSaga
    updatePostAcqWorkflowTasks: { // Triggers clientTxnOppsUpdateSaga
      reducer: () => { },
      prepare: (tasks, actionResult) => ({ payload: { tasks, actionResult } })
    },
    closePostAcqTasks: () => { }, // Triggers postAcqTaskStatusSaga
    openPostAcqTasks: () => { }, // Triggers postAcqTaskStatusSaga
    deletePostAcqTasks: () => { }, // Triggers deletePostAcqTasksSaga
    assignPostAcqTasksTo: { // Triggers taskAssignmentSaga
      reducer: () => { },
      prepare: (taskType, taskIds, assignToUserId, initiatedUserId) => ({ payload: { taskType, taskIds, assignToUserId, initiatedUserId } })
    },
    unassignPostAcqTasks: { // Triggers taskAssignmentSaga
      reducer: () => { },
      prepare: (taskType, taskIds, initiatedUserId) => ({ payload: { taskType, taskIds, initiatedUserId } })
    },
    editPostAcqTasks: { // Triggers editPostAcqTasksSaga
      reducer: () => { },
      prepare: (taskIds, editField, newValue) => ({ payload: { taskIds, editField, newValue } })
    }
  }
});

export default slice.reducer;

export const postAcqWorkflowActions = slice.actions;

// Memoized selectors
export const postAcqWorkflowSelectors = {
  postAcqWorkflowGridSelectedLayoutIdSelector: createSelector(
    state => state.postAcqWorkflowState,
    postAcqWorkflowState => postAcqWorkflowState.postAcqWorkflowGridSelectedLayoutId
  ),
  postAcqWorkflowGridOldestLayoutIdSelector: createSelector(
    state => state.postAcqWorkflowState.postAcqWorkflowGridLayouts,
    postAcqWorkflowGridLayouts => {
      if (!postAcqWorkflowGridLayouts?.length) return null;
      return postAcqWorkflowGridLayouts.slice().sort((a,b) => a.createdOn - b.createdOn)[0].id;
    }
  ),
  isFetchingPostAcqWorkflowDataSelector: createSelector(
    state => state.clientTxnOppsState.isFetchingPostAcqWorkflowData,
    isFetchingPostAcqWorkflowData => isFetchingPostAcqWorkflowData
  )
};